import { Link } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowRightLeft,
  ArrowUpRight,
  Lock,
  Trash2,
  Undo2,
  Unlock,
} from "lucide-react";

import type { InventoryTransaction, TransactionType } from "@/api/types";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import {
  formatDateTime,
  formatQuantity,
  formatReasonCode,
  formatTransactionType,
  formatUnit,
  isNegativeQuantity,
  isZeroQuantity,
} from "@/lib/format";

const ICONS: Record<TransactionType, typeof ArrowDownLeft> = {
  RECEIVE: ArrowDownLeft,
  RETURN: ArrowDownLeft,
  ISSUE: ArrowUpRight,
  CONSUME: ArrowUpRight,
  SCRAP: Trash2,
  TRANSFER: ArrowRightLeft,
  ADJUSTMENT: Undo2,
  RESERVE: Lock,
  RELEASE_RESERVATION: Unlock,
  SUPPLIER_RETURN: ArrowUpRight,
};

/**
 * The history as a table (Frontend Spec §22).
 *
 * Date, actor, event, article, quantity, reason, reference — the columns a
 * technician reads across. On a wide screen this is what a WMS looks like; the
 * compact list is for the phone.
 */
export function HistoryTable({ transactions }: { transactions: InventoryTransaction[] }) {
  return (
    <TableShell>
      <thead>
        <tr>
          <Th className="w-40 whitespace-nowrap">Date</Th>
          <Th className="w-44">Mouvement</Th>
          <Th>Article</Th>
          <Th align="right" className="w-28">
            Quantité
          </Th>
          <Th className="hidden w-44 @3xl:table-cell">Motif</Th>
          <Th className="hidden w-36 @4xl:table-cell">Par</Th>
          <Th className="hidden @5xl:table-cell">Détails</Th>
        </tr>
      </thead>
      <tbody>
        {transactions.map((transaction) => {
          const Icon = ICONS[transaction.transaction_type] ?? ArrowRightLeft;
          const isReservation = !isZeroQuantity(transaction.reserved_delta);
          const amount = isReservation ? transaction.reserved_delta : transaction.quantity_delta;
          const negative = isNegativeQuantity(amount);
          const before = transaction.metadata_json.book_quantity_before;
          const after = transaction.metadata_json.book_quantity_after;

          return (
            <Tr key={transaction.id}>
              <Td className="whitespace-nowrap text-[12px] text-ink-muted">
                {formatDateTime(transaction.created_at)}
              </Td>
              <Td>
                <span className="flex items-center gap-2">
                  <span
                    className={
                      negative
                        ? "flex size-7 shrink-0 items-center justify-center rounded-md bg-danger-subtle text-status-out"
                        : "flex size-7 shrink-0 items-center justify-center rounded-md bg-success-subtle text-status-available"
                    }
                  >
                    <Icon className="size-3.5" aria-hidden />
                  </span>
                  <span className="text-[13px] text-ink">
                    {formatTransactionType(transaction.transaction_type)}
                  </span>
                </span>
              </Td>
              <Td>
                <Link
                  to={`/products/${transaction.product_id}`}
                  className="text-[13px] text-ink hover:text-brand"
                >
                  {transaction.product_name || "—"}
                </Link>
              </Td>
              <Td align="right">
                <span
                  className={
                    negative
                      ? "tnum text-[14px] font-semibold text-status-out"
                      : "tnum text-[14px] font-semibold text-status-available"
                  }
                >
                  {negative ? "" : "+"}
                  {formatQuantity(amount)}
                </span>
                <span className="ml-1 text-[10px] text-ink-subtle">
                  {isReservation ? "rés." : formatUnit(transaction.unit)}
                </span>
              </Td>
              <Td className="hidden text-[12px] text-ink-muted @3xl:table-cell">
                {formatReasonCode(transaction.reason_code)}
              </Td>
              <Td className="hidden truncate text-[12px] text-ink-muted @4xl:table-cell">
                {transaction.created_by_label}
              </Td>
              <Td className="hidden text-[12px] text-ink-subtle @5xl:table-cell">
                <span className="flex flex-wrap items-center gap-2">
                  {transaction.reverses_transaction_id && (
                    <StatusBadge tone="warning">Contre-passation</StatusBadge>
                  )}
                  {typeof before === "string" && (
                    <span className="tnum">
                      {formatQuantity(before)} → {formatQuantity(String(after ?? ""))}
                    </span>
                  )}
                  {transaction.idempotency_key && (
                    <span
                      className="font-mono text-[10px]"
                      title="Clé d'idempotence : un rejeu de cette requête ne rejoue pas le mouvement."
                    >
                      {transaction.idempotency_key}
                    </span>
                  )}
                </span>
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </TableShell>
  );
}
